import type { MutableRefObject } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import html2Canvas from 'html2canvas';

import type { KpiInsight } from 'src/types/planAction';
import useSurveySettings from 'src/hooks/useSurveySettings';
import delay from 'src/helper/delay';
import { downloadPPT } from 'src/helper/pptgen';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { stringifyFilters } from 'src/components/planAction/PlanActionStatisticsPage/helpers';

interface UseGeneratePPTX {
  firstStatRef: MutableRefObject<any>;
  secondStatRef: MutableRefObject<any>;
  kpiStatusRef: MutableRefObject<any>;
  top5KpisRef: MutableRefObject<any>;
  statistics: { loading: boolean; kpiInsights: KpiInsight[] };
  setShowPPTInfoModal: (value: boolean) => void;
  setSelectedKPI: (value: number) => void;
  page: number;
  setPage: (value: number) => void;
}

const useGeneratePPTX = ({
  firstStatRef,
  secondStatRef,
  kpiStatusRef,
  top5KpisRef,
  statistics,
  setShowPPTInfoModal,
  setSelectedKPI,
  page,
  setPage,
}: UseGeneratePPTX) => {
  const { t, i18n } = useTranslation('planAction');
  const { selectedOrgUnit, planActionStatus } = usePlanActionContext();
  const { currentSurvey } = useUserContext();
  const [pptGeneration, setPPTGeneration] = useState(false);
  const { filterGroups } = useFilterContext();
  const { minRespondentAmount } = useSurveySettings();

  const generatePPT = async (lang: any) => {
    if (!selectedOrgUnit || !planActionStatus?.value) return;

    // When we start this function we want to set 'pptGeneration' to true
    setPPTGeneration(true);
    // Basic settings regarding viewport
    const w = '2560px';
    const h = '1440px';

    // We need to 'save' a few settings for later
    const currentPage = page;
    const currentLang = i18n.language;

    // Stringify the current selected filters
    const { orgUnit: _, ...filterGroupsWithoutOrgUnit } = filterGroups;
    const filterLine = stringifyFilters(filterGroupsWithoutOrgUnit, t);
    // If we change the language we need to wait for the api call to be done
    // That's why we call recursive this function via a useEffect hook
    if (currentLang === lang || (!statistics.loading && pptGeneration)) {
      // This is the information for the header line (as seen on top of the tabs)
      const selectedHeader = {
        orgUnit: {
          label: t('planAction:Header.ActionPlansFor'),
          value: `${selectedOrgUnit.name} (${selectedOrgUnit.org_unit_id})`,
        },
        responsibleManager: {
          label: t('planAction:Header.ResponsibleManager'),
          value: planActionStatus.value.manager
            ? planActionStatus.value.manager.name
            : t('Header.NoManagerSelected'),
        },
        email: {
          label: t('planAction:Header.Email'),
          value: planActionStatus.value.manager
            ? planActionStatus.value.manager.email
            : '-',
        },
        priority: {
          label: t('planAction:Header.Priority'),
          value: planActionStatus.value.priority,
        },
        headcount: {
          label: t('planAction:Header.Headcount'),
          value:
            planActionStatus.value.headcount ||
            `n<${minRespondentAmount}/NegSel`,
        },
      };

      const additionalText = {
        firstSlide: t('Statistics.Status.Waterfall.FollowUpStatus'),
        priorityValue: planActionStatus.value.priority
          ? t('common:yes')
          : t('common:no'),
      };

      /* Now we can 'fetch'/'grap'/'screenshot' every tab. */

      // First we scale the document.body so big, that we don't get squished
      // diagrams, and we have a good image foundation regarding resolution
      const bodyCopy = document.body;
      if (`${bodyCopy.clientWidth}px` < w) {
        bodyCopy.style.width = w;
        bodyCopy.style.height = h;
      }
      // Change zoom to 100%
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      document.body.style.zoom = '100%';

      /* FirstPage */
      setPage(0);
      await delay(1000);
      const firstTabWrapper = document.getElementById('org-unit-waterfall');
      if (firstTabWrapper) {
        // Scale to a large value first otherwise the canvas will not react
        firstTabWrapper.style.height = '2000px';
        // We need to wait for animations to be done.
        await delay(1000);
        firstTabWrapper.style.height = '740px';
        // We need to wait for animations to be done.
        await delay(1000);
      }

      // Then we can convert the current visible canvas into an image
      const firstStatCanvas =
        firstStatRef.current.ctx.canvas.getContext('2d').canvas;
      const firstImageData = {
        dataUrl: firstStatCanvas.toDataURL(),
        height: firstStatCanvas.height,
        width: firstStatCanvas.width,
      };
      if (firstTabWrapper) {
        // firstTabWrapper.style.height = 'auto';
        firstTabWrapper.style.width = 'auto';
      }
      /* Second Page */
      setPage(1);
      await delay(1000);

      // We also hide the tooltip buttons.
      const coverageStatistics = document.getElementById('coverage-statistics');
      if (coverageStatistics) {
        const toolTipButton = Array.from(
          coverageStatistics.querySelectorAll('button'),
        );
        toolTipButton.forEach((element) => (element.style.display = 'none')); // eslint-disable-line
      }

      // After all decoration elements are hidden we can grab the second div.
      const secondStatElement = secondStatRef.current;
      // Then convert it into a canvas.
      const secondElement = await html2Canvas(secondStatElement);

      /* Third Page */
      setPage(2);
      await delay(1000);

      // Sort the Kpi Statements
      const sortedKpis = [...statistics.kpiInsights].sort(
        (a, b) => b.actionCount - a.actionCount,
      );
      const thirdPageElements = [];
      const top5KpiElement = top5KpisRef.current;
      const top5KpiImage = await html2Canvas(top5KpiElement);

      for (let i = 0; i < sortedKpis.length; i += 1) {
        setSelectedKPI(i);

        // Get the div from the third page.
        const thirdStatElement = kpiStatusRef.current;

        // Convert it into a canvas.
        thirdPageElements.push(await html2Canvas(thirdStatElement));
      }
      // After we 'got' all three tabs we can start with generating the ppt.
      if (secondElement && thirdPageElements.length > 0 && top5KpiImage) {
        // But first we need to read the percentages back inside the picture
        const secondStatCanvas = secondElement.getContext('2d')?.canvas;
        // We create a new canvas to combine image and percentages
        const secondCanvasNew = document.createElement('canvas');
        // We need to set the size of the canvas to be the same as the original one
        // where we grab the image from.
        const { height, width } = secondStatCanvas || { height: 0, width: 0 };
        secondCanvasNew.setAttribute('height', String(height));
        secondCanvasNew.setAttribute('width', String(width));
        const ctx = secondCanvasNew.getContext('2d');

        if (ctx) {
          // Place the image/canvas inside the new canvas
          if (secondStatCanvas) {
            ctx.drawImage(secondStatCanvas, 0, 0, width, height);
          }
          ctx.font = '30px Inter';

          // Now onto painting some lines as visual borders
          // Horizontal lines
          ctx.moveTo(0, height / 3);
          ctx.lineTo(width, height / 3);
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#bfbfbf';
          ctx.stroke();

          ctx.moveTo(0, (height / 3) * 2);
          ctx.lineTo(width, (height / 3) * 2);
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#bfbfbf';
          ctx.stroke();
        }

        // convert the newly created canvas and the thirdStatCanvas to images
        const secondImageData = ctx
          ? {
              dataUrl: ctx.canvas.toDataURL(),
              height: ctx.canvas.height,
              width: ctx.canvas.width,
            }
          : null;
        const thirdImageData = thirdPageElements.map((element) => {
          const canvas = element.getContext('2d')?.canvas;
          return {
            dataUrl: canvas ? canvas.toDataURL() : null,
            height: canvas ? canvas.height : 0,
            width: canvas ? canvas.width : 0,
          };
        });

        const top5KpiImageCanvas = top5KpiImage.getContext('2d')?.canvas;
        const top5KpiCanvas = {
          dataUrl: top5KpiImageCanvas ? top5KpiImageCanvas.toDataURL() : null,
          height: top5KpiImageCanvas ? top5KpiImageCanvas.height : 0,
          width: top5KpiImageCanvas ? top5KpiImageCanvas.width : 0,
        };
        // Generate PPTX
        downloadPPT({
          additionalText,
          filterLine,
          firstImage: firstImageData,
          survey: currentSurvey,
          orgUnit: selectedOrgUnit,
          secondImage: secondImageData,
          selectedHeader,
          thirdImage: thirdImageData,
          top5KpiImage: top5KpiCanvas,
        });

        // Set body size back to original
        bodyCopy.style.width = '';
        bodyCopy.style.height = '';
        // Change zoom to original
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        document.body.style.zoom = '';
        // Go back to original viewed page
        setPage(currentPage);
        // Close Modal
        setShowPPTInfoModal(false);
        setPPTGeneration(false);
      }
    }
  };

  return [pptGeneration, generatePPT];
};

export default useGeneratePPTX;
